/**
 * Sentry — error monitoring para el frontend Next Valley.
 *
 * Captura automáticamente errores no manejados, errores de React (vía
 * ErrorBoundary) y errores de queries Supabase que loggeamos manualmente.
 *
 * Configurar el DSN en Vercel:
 *   1. Cuenta en https://sentry.io → crea un proyecto "react"
 *   2. Copia el DSN (formato https://xxx@xxx.ingest.sentry.io/xxx)
 *   3. En Vercel → Settings → Environment Variables → agrega:
 *        VITE_SENTRY_DSN = <tu DSN>
 *        VITE_SENTRY_ENV = production
 *   4. Re-deploy
 *
 * Sin DSN, Sentry queda inactivo silenciosamente — no rompe la app.
 */
import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry() {
  if (initialized) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    // Sin DSN configurado — Sentry deshabilitado (común en local dev)
    console.info('[Sentry] DSN no configurado, monitoring deshabilitado.')
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Performance monitoring (opcional pero útil para detectar páginas lentas)
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,        // privacidad por default
        blockAllMedia: true,
      }),
    ],

    // Sample rates conservativos para no quemar el free tier (5k/mes)
    tracesSampleRate: 0.1,        // 10% de las navegaciones
    replaysSessionSampleRate: 0,  // 0% sesiones normales
    replaysOnErrorSampleRate: 1,  // 100% cuando hay error → ver replay

    // Filtros para no inundar Sentry con ruido
    ignoreErrors: [
      // Errores de auth recoverables que el usuario ni nota
      'AbortError',
      'Non-Error promise rejection captured',
      // ResizeObserver es ruido sin acción posible
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Errores de network del usuario, no nuestros
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Errores de extensiones del browser
      /chrome-extension:\/\//,
      /^safari-extension:\/\//,
    ],

    beforeSend(event, hint) {
      // Suprime errores con stack trace solo en extensiones de Chrome
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
      if (frames.length > 0 && frames.every(f => /extension/i.test(f.filename ?? ''))) {
        return null
      }
      return event
    },
  })

  initialized = true
  console.info('[Sentry] inicializado.')
}

/**
 * Captura manual de error con contexto (para queries de Supabase, edge function
 * calls, etc. — cosas que el browser no capturaría automáticamente).
 *
 * Ejemplo:
 *   try { await supabase... } catch (err) {
 *     captureError(err, { tags: { area: 'caja-dashboard' }, extra: { id } })
 *   }
 */
export function captureError(error, context = {}) {
  // Siempre loggeamos en consola también
  console.error(error, context)
  if (!initialized) return
  Sentry.captureException(error, {
    tags: context.tags,
    extra: context.extra,
    user: context.user,
  })
}

/**
 * Setea info del usuario en Sentry. Útil para filtrar errores por user.
 * Llamar después de login.
 */
export function setSentryUser(user) {
  if (!initialized) return
  if (!user) { Sentry.setUser(null); return }
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.full_name,
    role: user.role,
  })
}

/**
 * Sentry ErrorBoundary — wrap tu App con esto para capturar errores
 * de render de React.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary
